import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ImageInfo } from '../models';

/**
 * One catalogue tag — a home-menu shortcut that groups products/subcategories
 * under a campaign ("Natal", "Novidades") WITHOUT moving them out of their
 * categories. Mirrors GET /api/tags rows: name/description come translated
 * from the backend `translations` table, image from `cms_images`.
 */
export interface TagInfo {
  id: number;
  ref: string;
  name: string | null;
  /** Short campaign label ("Época", "Novo") — rendered as the tile badge. */
  description: string | null;
  image: ImageInfo | string | null;
  /** Named accent: 'green' | 'gold' | 'orange'; anything else renders green. */
  colour: string | null;
  /** null = every brand tab; otherwise product_types.brand_id semantics. */
  brandId: number | null;
  orderView: number;
  itemCount: number | null;
}

export interface TagItemRoute {
  typeId: number;
  /** Query params for /products/:typeId — productId / subCategoryId / collectionId. */
  params: Record<string, number>;
}

/** One item inside a tag. The backend already resolved where it routes. */
export interface TagItem {
  kind: 'product' | 'subcategory';
  id: number;
  ref: string;
  typeId: number;
  subTypeId: number | null;
  orderView: number;
  route: TagItemRoute;
}

export interface TagDetail extends TagInfo {
  items: TagItem[];
}

export type TagVariant = 'green' | 'gold' | 'orange';

/**
 * Visual presets keyed by ref/name keywords — reproduce the campaign look of
 * designs/products-featured.html (Natal = green tree, Comunhões = gold cross,
 * Novidades = orange sparkle) without any CMS setup. An explicit `colour`
 * always wins over the preset variant, and a CMS image always wins over the
 * preset icon.
 */
interface TagVisualPreset {
  match: RegExp;
  variant: TagVariant;
  iconPaths: string[];
}

const TAG_VISUAL_PRESETS: TagVisualPreset[] = [
  { match: /natal|christmas|xmas|weihnacht/i, variant: 'green', iconPaths: ['M12 3 6 11h3l-4 6h5v4h4v-4h5l-4-6h3z'] },
  { match: /comunh|communion|komm?union/i, variant: 'gold', iconPaths: ['M12 3v18M7 8h10M9 21h6'] },
  { match: /novidad|novo|new|nouveaut|novit|neuheit|nieuw/i, variant: 'orange', iconPaths: ['M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z'] }
];

/** Stroke tag glyph (design language) for campaigns without a preset. */
const TAG_DEFAULT_ICON_PATHS: string[] = [
  'M20.6 13.4 11 3.8A2 2 0 0 0 9.6 3H5a2 2 0 0 0-2 2v4.6c0 .5.2 1 .6 1.4l9.6 9.6a2 2 0 0 0 2.8 0l4.6-4.6a2 2 0 0 0 0-2.8Z',
  'M7.5 7.5h.01'
];

function findPreset(tag: Pick<TagInfo, 'ref' | 'name'>): TagVisualPreset | undefined {
  return TAG_VISUAL_PRESETS.find(p => p.match.test(tag.ref) || (!!tag.name && p.match.test(tag.name)));
}

/** Accent variant: explicit colour first, then ref/name preset, then green. */
export function tagVisualVariant(tag: Pick<TagInfo, 'ref' | 'name' | 'colour'>): TagVariant {
  const colour = (tag.colour || '').toLowerCase();
  if (colour === 'green' || colour === 'gold' || colour === 'orange') return colour;
  return findPreset(tag)?.variant ?? 'green';
}

/** SVG path data for the tile icon (24×24 viewBox, stroke style). */
export function tagIconPaths(tag: Pick<TagInfo, 'ref' | 'name'>): string[] {
  return findPreset(tag)?.iconPaths ?? TAG_DEFAULT_ICON_PATHS;
}

/**
 * Catalogue tags (campaign shortcuts). Read-only: two endpoints, both already
 * filtered server-side by country/customer/partner and "active now" — the
 * frontend never re-decides visibility, it only narrows brand-scoped tags to
 * the active home tab.
 */
@Injectable({ providedIn: 'root' })
export class TagsService {
  private readonly api = inject(ApiService);

  private readonly _tags = signal<TagInfo[]>([]);
  readonly tags = this._tags.asReadonly();

  /** Locale the cached list was fetched for — tag names come translated. */
  private loadedLocale: string | null = null;
  private loading = false;

  /**
   * Fetch the live tags once per locale. No brandId param — the home filters
   * per tab client-side against its categories' brand ids, so one fetch
   * serves every tab. Errors leave the list empty (no tag tiles, no noise).
   */
  loadTags(locale: string): void {
    if (this.loading || this.loadedLocale === locale) return;
    this.loading = true;
    this.api.get<TagInfo[]>('/tags').subscribe({
      next: (tags) => {
        this.loading = false;
        this.loadedLocale = locale;
        this._tags.set(Array.isArray(tags) ? tags : []);
      },
      error: () => {
        this.loading = false;
        this.loadedLocale = locale;
        this._tags.set([]);
      }
    });
  }

  /** One tag with its items; null on 404 (expired / empty for this user). */
  getTag(id: number): Observable<TagDetail | null> {
    return this.api.get<TagDetail>(`/tags/${id}`).pipe(
      catchError(() => of(null))
    );
  }
}
